import React, { useState, useRef } from "react"
import {
  UseFormRegister,
  FieldErrors,
  Path,
  UseFormWatch,
  UseFormSetValue,
} from "react-hook-form"
import * as toastr from "toastr"
import { useTranslation } from "react-i18next"

import { useUploadPdfMutation } from "hooks/api/survey/use-upload-pdf-mutation"
import { useS3UploadMutation } from "hooks/api/survey/use-s3-upload-mutation"
import { useDragAndDrop } from "hooks/utils/use-drag-and-drop"
import { getLocalizedText } from "utils/i18n/get-localized-text"
import { PlaceOrderFormData, Question, PdfUploadFieldValue } from "types"

interface PdfUploadQuestionProps {
  question: Question
  register: UseFormRegister<PlaceOrderFormData>
  errors: FieldErrors<PlaceOrderFormData>
  watch: UseFormWatch<PlaceOrderFormData>
  setValue: UseFormSetValue<PlaceOrderFormData>
  ticketId: number
}

const PdfUploadQuestion: React.FC<PdfUploadQuestionProps> = ({
  question,
  register,
  errors,
  watch,
  setValue,
  ticketId,
}) => {
  const { t } = useTranslation()
  const [uploadProgress, setUploadProgress] = useState(0)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const uploadPdfMutation = useUploadPdfMutation()
  const s3UploadMutation = useS3UploadMutation()

  const isProcessing = uploadPdfMutation.isPending || s3UploadMutation.isPending

  const questionLabel = getLocalizedText(question.text, "Question")

  const fieldName =
    `tickets.${ticketId}.${question.uuid}` as Path<PlaceOrderFormData>
  const fieldError = (errors as any).tickets?.[ticketId]?.[question.uuid]

  // Watch current file data (now storing the upload response)
  const fieldValue = watch(fieldName)
  const fileData =
    typeof fieldValue === "object"
      ? (fieldValue as PdfUploadFieldValue)
      : undefined

  const validateFile = (file: File): string | null => {
    // Check file type
    if (
      file.type !== "application/pdf" &&
      !file.name.toLowerCase().endsWith(".pdf")
    ) {
      return t("validation.pdf_file_only", "Please select a PDF file")
    }

    // Check file size (10MB limit)
    const maxSize = 10 * 1024 * 1024 // 10MB in bytes
    if (file.size > maxSize) {
      return t("validation.file_size_limit", "File size must be less than 10MB")
    }

    return null
  }

  const processFile = async (file: File) => {
    const validation = validateFile(file)
    if (validation) {
      toastr.error(validation)
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
      return
    }

    setUploadProgress(0)

    try {
      // Step 1: Get presigned URL from backend (allocate 10% of progress)
      const result = await uploadPdfMutation.mutateAsync({
        file,
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            // First 10% of progress bar for getting presigned URL
            const percentCompleted = Math.round(
              (progressEvent.loaded * 10) / progressEvent.total
            )
            setUploadProgress(percentCompleted)
          }
        },
      })

      // Step 2: Upload directly to S3 using presigned URL (allocate 90% of progress)
      if (result.presigned_url) {
        await s3UploadMutation.mutateAsync({
          file,
          presignedUrl: result.presigned_url,
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              // Remaining 90% of progress bar for S3 upload
              const percentCompleted = Math.round(
                10 + (progressEvent.loaded * 90) / progressEvent.total
              )
              setUploadProgress(percentCompleted)
            }
          },
        })
      }

      // Store the response data (id and pdf_link)
      setValue(fieldName, result, { shouldValidate: true })
      toastr.success(
        t("validation.pdf_uploaded", "PDF uploaded successfully")
      )
    } catch (error) {
      console.error("PDF upload failed:", error)
      toastr.error(
        t("validation.upload_failed", "Upload failed. Please try again.")
      )
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
    } finally {
      setUploadProgress(0)
    }
  }

  const { isDragOver, dragHandlers } = useDragAndDrop({
    onDrop: processFile,
    disabled: isProcessing,
  })

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0]
    if (!file) return
    await processFile(file)
  }

  const handleRemoveFile = () => {
    setValue(fieldName, undefined as any, { shouldValidate: true })
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const getFileName = () => {
    const key = fileData?.presigned_url?.fields?.key
    if (!key) return null
    const fullFilename = key.split("/").pop() || ""
    const plusIndex = fullFilename.indexOf("+")
    return plusIndex > 0 ? fullFilename.substring(plusIndex + 1) : fullFilename
  }

  return (
    <div>
      <label className="block mb-1 text-sm font-medium text-theme_text">
        {questionLabel}
        {question.requires_answer && (
          <span className="ml-1 text-red-500">*</span>
        )}
      </label>

      {!fileData ? (
        <div className="mt-1">
          <div
            className={`flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md transition-colors ${
              isDragOver
                ? "border-theme_accent bg-theme_accent/5 cursor-pointer"
                : "border-input_border hover:border-theme_text_medium cursor-pointer"
            }`}
            onClick={() => fileInputRef.current?.click()}
            {...dragHandlers}
          >
            <div className="space-y-1 text-center">
              <svg
                className="mx-auto w-12 h-12 text-theme_text_medium"
                stroke="currentColor"
                fill="none"
                viewBox="0 0 48 48"
                aria-hidden="true"
              >
                <path
                  d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <div className="flex flex-col justify-center text-sm text-theme_text_medium_plus">
                <span className="relative font-medium bg-white rounded-md cursor-pointer text-theme_accent hover:text-theme_accent_light">
                  {t("pdf_upload.upload_text", "Upload a PDF file")}
                </span>
                <p className="pl-1">
                  {t("pdf_upload.drag_drop", "or drag and drop")}
                </p>
              </div>
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,application/pdf"
            onChange={handleFileChange}
            className="sr-only"
            disabled={isProcessing}
          />

          {isProcessing && (
            <div className="mt-3">
              <div className="flex justify-between items-center mb-1 text-sm text-theme_text_medium_plus">
                <span>{t("pdf_upload.uploading", "Uploading...")}</span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="w-full h-2 bg-theme_background_lighter rounded-full">
                <div
                  className="h-2 rounded-full transition-all duration-300 bg-theme_accent"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="flex justify-between items-center p-3 mt-1 min-w-0 rounded-md border bg-theme_background_lighter border-input_border">
          <div className="flex flex-1 items-center mr-2 min-w-0">
            <svg
              className="flex-shrink-0 mr-2 w-5 h-5 text-red-500"
              fill="currentColor"
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                clipRule="evenodd"
              />
            </svg>
            <span className="min-w-0 text-sm truncate text-theme_text">
              {getFileName()}
            </span>
          </div>
          <button
            type="button"
            onClick={handleRemoveFile}
            className="flex-shrink-0 text-sm font-medium text-red-600 hover:text-red-800"
          >
            {t("pdf_upload.remove", "Remove")}
          </button>
        </div>
      )}

      {/* Hidden input for form validation */}
      <input
        type="hidden"
        {...register(fieldName, {
          required: question.requires_answer
            ? t("validation.pdf_required", "Please upload a PDF file")
            : false,
          validate: (value) => {
            if (!question.requires_answer && !value) {
              return true
            }
            if (question.requires_answer && !value) {
              return t("validation.pdf_required", "Please upload a PDF file")
            }
            // Ensure we have valid data with an ID
            if (
              value &&
              (typeof value !== "object" ||
                !("id" in value) ||
                !("pdf_file" in value))
            ) {
              return t("validation.pdf_invalid", "Invalid PDF file")
            }
            return true
          },
        })}
      />

      {fieldError && (
        <p className="mt-1 text-sm text-red-500" role="alert">
          {fieldError.message}
        </p>
      )}

      <p className="mt-1 text-xs text-theme_text_medium_plus">
        {t(
          "pdf_upload.accepted_format",
          "Accepted format: PDF files only, maximum size: 10MB"
        )}
      </p>
    </div>
  )
}

export { PdfUploadQuestion }
